/**
 * Business Valuator wire types (VALUATOR-UI-1).
 *
 * These mirror `apps/api/routers/_valuation_models.py`,
 * `_valuation_profiles.py` and the payload `_valuation_engine.py` composes.
 * Nothing here is computed client-side: every number the Valuation screens
 * display comes off the API, because the signed methodology lives in
 * `core/valuation` and the browser is not allowed a second opinion on it.
 */

// ── Entitlement ───────────────────────────────────────────────────────────────

/**
 * Mirrors `CAPABILITIES['valuation:read']` in `apps/api/entitlements.py` — who may
 * open the Valuation surface.
 *
 * RBAC-1 dropped plain `cpa`: a QoE role must never implicitly grant Valuation
 * (CROSS_PRODUCT_ROUTING §1). A CPA who should see Valuation gets `valuation_signer`.
 *
 * This is a role check only — the second half of the server's gate. The server
 * also asks whether the ACCOUNT holds the valuation product at all; that half is
 * answered by the `products` array on /api/v1/me, not by this list.
 */
export const VALUATION_READ_ROLES = [
  'super_admin',
  'valuation_signer',
  'valuation_owner',
] as const

/**
 * Mirrors `CAPABILITIES['valuation:conclusion']` — who may open/run a CONCLUSION
 * of value (AICPA SSVS No. 1 valuation engagement) and who may sign one off.
 * A `valuation_owner` is deliberately absent: an owner runs calculations.
 *
 * SIGNER-IDENTITY-BINDING-1 R1 (2026-08-31): `super_admin` was removed. The
 * platform operator has no attested valuation credential and no firm — a live
 * DEV snapshot (cce58b6a-06d2-4660-9121-03958cd6a6ce) was signed_role
 * super_admin on a `divergent` conclusion before this fix.
 */
export const VALUATION_CONCLUSION_ROLES = [
  'valuation_signer',
] as const

/**
 * Mirrors `CAPABILITIES['valuation:override']` — who may adjust a calculated
 * input (VAL-PATH-SIGNER P7a: the signer's judgement; the owner is in
 * `VALUATION_READ_ROLES` but not here). See `require_override_role` in
 * `apps/api/authz.py`.
 */
export const VALUATION_OVERRIDE_ROLES = [
  'super_admin',
  'valuation_signer',
] as const

/** The two SSVS deliverables. Fixed at engagement creation; never patchable. */
export type ValueKind = 'calculated' | 'concluded'

// ── Signed industry profiles (GET /v1/valuation/profiles) ─────────────────────

export interface ProfileBand {
  low: number
  high: number
  default_anchor: number
  midpoint: number
  tier_label: string | null
  tier_min: number | null
  tier_max: number | null
}

export interface ProfileMultiple {
  key: string
  /** Words for `key` (VAL-SCREEN-WORDS-1); absent on an older server. */
  label?: string
  tier_basis: string | null
  is_tiered: boolean
  envelope_low: number
  envelope_high: number
  bands: ProfileBand[]
}

export interface ProfileGovernance {
  activated_by: string | null
  activated_by_role: string | null
  activated_on: string | null
  citations: string[]
}

export interface ValuationProfile {
  profile_id: string
  industry_code: string
  display_name: string
  version: string
  status: 'draft' | 'active' | 'retired'
  /** False for a draft/retired profile — listed for visibility, never usable. */
  selectable: boolean
  /** Set when an active profile is not in the caller's firm methodology version. */
  not_selectable_reason?: string | null
  is_generic_fallback: boolean
  premise_default: string
  premise_label?: string
  driver_tilts: string[]
  normalization_flags: string[]
  crossover_override: number | null
  approach_weights_override: { income: number; market: number; dcf: number } | null
  governance: ProfileGovernance
  multiples: ProfileMultiple[]
  source_path: string | null
}

export interface ValuationProfilesResponse {
  profiles: ValuationProfile[]
  generic_industry_code: string
  selectable_count: number
}

// ── Signed methodology (GET /v1/valuation/methodology) ────────────────────────

/**
 * Transcribed signed constants from `core/valuation/methodology.py`. The intake
 * form renders FROM this — the driver names, the points ceiling and the
 * overridable field list are never written down in the client, because a
 * hardcoded copy would silently fork the signed methodology.
 */
export interface HouseDefault {
  value: number
  as_of: string
  /** The client-facing sentence shown under the field. */
  note: string
}

export interface ValuationMethodology {
  methodology_version: string
  triangulation_weights: { income: number; market: number; dcf_cross_check: number }
  convergence_rule: { max_spread_pct: number; low_band_pct: number; high_band_pct: number }
  value_driver_rubric: {
    driver_names: string[]
    max_score_per_driver: number
    max_total_score: number
    bands: Array<{
      label: string
      min_score: number
      max_score: number
      multiple_adjustment: number
    }>
  }
  earnings_basis_rule: {
    crossover_earnings_threshold: number
    basis_below_threshold: string
    basis_at_or_above_threshold: string
  }
  customer_concentration_trigger_pct: number
  overridable_fields: string[]
  /** FIX_VAL-OWNER-INPUTS-2 — house starting values, business owner only (null otherwise). Keys are draft field names. */
  input_defaults?: Record<string, HouseDefault> | null
  /** The risk-free rate with its source and as-of date, served to every role. */
  risk_free_reference?: HouseDefault
  /**
   * DEPRECATED (VALUATION-B3). Still reported by the API for pre-B3 clients;
   * no screen composes `driver_score.<name>` into an override field list any
   * more — see the guardrail note in `_IntakeDrivers.tsx`.
   */
  driver_score_override_prefix: string
  driver_questionnaire: DriverQuestionnaire
}

/**
 * VALUATION-B3 guardrail 1 — the intake surface for value drivers.
 *
 * Note what this shape does NOT contain: anywhere for the client to put a
 * score. The valuator picks an option; the engine recomputes the score from
 * the option's `value` server-side. `points` below is display only — editing
 * it in the browser changes nothing about the number that prints.
 */
export interface DriverAnswerOption {
  value: string
  label: string
  points: number
}

export interface DriverQuestion {
  question_id: string
  driver: string
  section: string
  prompt: string
  help_text: string
  /** "ASK", "DERIVE" or "ASK + DERIVE" — from the signed intake question set. */
  ask_or_derive: string
  max_points: number
  /**
   * VAL-INTAKE-SCOPE-1: true for one of the driver's SCORED questions —
   * `score_questionnaire()` computes the driver's score from required
   * answers only. false means corroborating: still worth answering and
   * still shown, but it never moves the score and never blocks the wizard's
   * Next button. Config data — never a hardcoded question-id list.
   */
  required: boolean
  options: DriverAnswerOption[]
}

export interface DriverQuestionnaire {
  config_version: string
  sections: string[]
  max_total_score: number
  questions: DriverQuestion[]
}

// ── Engagements ───────────────────────────────────────────────────────────────

export interface ValuationEngagement {
  valuation_engagement_id: string
  firm_id: string | null
  subject_company_name: string
  industry: string | null
  /** Read-time words beside the stored industry code (VAL-SCREEN-WORDS-1). */
  industry_label?: string | null
  /** VAL-SNAPSHOT-2: a Snapshot grant exists; credit runs six months from it. */
  snapshot_paid?: boolean
  full_report_paid?: boolean
  /** FIX_VAL-SNAPSHOT-1C: a Snapshot download exists; set for every role, carries no price. */
  snapshot_available?: boolean
  snapshot_credit_amount?: number | null
  snapshot_credit_expires_on?: string | null
  snapshot_credit_live?: boolean
  /** VAL-PATH-ROUTES (P1): server-computed step facts — the step bar, left nav
   *  and dashboard card read only these (U3). */
  company_done?: boolean
  years_read?: number
  /** VAL-PATH-FOOTER (P2a): years_read meets the server's minimum. */
  years_enough?: boolean
  applied?: boolean
  questions_done?: boolean
  value_ready?: boolean
  /** VAL-STRIPE-GATE-1: owner only. false = the value fields are blanked until payment. */
  value_paid?: boolean | null
  /** VAL-FIX-PAY-BUGS-1 bug 3: owner/super_admin only - when the value was paid (UTC ISO). */
  payment?: { paid_at?: string | null; price_key?: string | null } | null
  /** VAL-TIERS-1-2: owner/super_admin only - the highest tier bought and what it includes, lowest first. */
  value_tier?: string | null
  value_tiers?: { tier: string; title: string; description: string }[] | null
  period_label: string | null
  status: string
  value_kind: ValueKind
  calculated_value_low: number | null
  calculated_value_high: number | null
  concluded_value_low: number | null
  concluded_value_high: number | null
  valuation_method: string | null
  valuation_method_label?: string | null
  notes: string | null
  effective_date: string | null
  created_at: string
  updated_at: string
  // VAL-ENGAGEMENT-PARTICULARS-1 — VS Section 100 engagement particulars.
  // Optional/nullable throughout: additive to an existing row shape, no
  // codec version bump (VAL-VALUATION-DATE-1 precedent).
  prepared_for: string | null
  purpose: string | null
  intended_users: string | null
  standard_of_value: string | null
  premise_of_value: string | null
  subject_interest: string | null
  interest_characteristics: string | null
  sources_relied_upon: string | null
  analyst_has_financial_interest: boolean
  fee_is_contingent: boolean
}

export interface CreateEngagementBody {
  subject_company_name: string
  industry?: string | null
  period_label?: string | null
  value_kind?: ValueKind
  // VAL-INTAKE-REQUIRED-1: required at create — see REQUIRED_PARTICULARS_FIELDS
  // in core/valuation/finalize_particulars.py. Omitting one is a 422; a
  // blank/whitespace value is a 400 (ValidationError, field-focused).
  purpose: string
  intended_users: string
  standard_of_value: string
  premise_of_value: string
  subject_interest: string
}

export interface UpdateEngagementBody {
  subject_company_name?: string
  industry?: string
  period_label?: string
  status?: string
  notes?: string
  effective_date?: string | null
  // VAL-ENGAGEMENT-PARTICULARS-1 — how Intake persists the ten particulars.
  prepared_for?: string | null
  purpose?: string | null
  intended_users?: string | null
  standard_of_value?: string | null
  premise_of_value?: string | null
  subject_interest?: string | null
  interest_characteristics?: string | null
  sources_relied_upon?: string | null
  analyst_has_financial_interest?: boolean
  fee_is_contingent?: boolean
}

// ── Run (POST /v1/valuation/engagements/{id}/run) ─────────────────────────────

/** One attributed CPA override. Every entry must carry a reason. */
export interface CpaOverride {
  field: string
  cpa_value: number
  reason: string
}

export interface RunBody {
  risk_free_rate: number
  company_specific_risk: number
  long_term_growth_rate: number
  equity_risk_premium?: number | null
  size_premium?: number | null
  normalized_earnings: number
  base_cash_flow?: number | null
  /**
   * VAL-INCOME-BASIS-FIX-1: annual cost to hire a market-rate replacement
   * to run the business. Required by the API (400) below the signed
   * SDE/EBITDA crossover; blank is allowed here and produces that 400 on
   * run, not a client-side block.
   */
  replacement_compensation?: number | null
  growth_rate: number
  projection_years?: number
  capitalized_earnings_weight?: number
  comp_multiples?: number[] | null
  raw_financials?: Record<string, unknown> | null
  adjustments?: Record<string, unknown>[] | null
  total_assets?: number | null
  total_liabilities?: number | null
  fair_value_adjustments?: number | null
  /** `question_id -> option value`. The only driver input the UI sends. */
  driver_answers?: Record<string, string> | null
  /**
   * DEPRECATED (VALUATION-B3), and rejected by the API when combined with
   * `driver_answers`. Kept on the type so a pre-B3 caller still typechecks.
   */
  driver_scores?: Record<string, number> | null
  cpa_overrides?: CpaOverride[] | null
}

export interface AppliedOverride {
  field: string
  default: number | null
  cpa_value: number
  reason: string
  cpa: string
  out_of_range: boolean
  note: string | null
}

export interface RunResult {
  valuation_engagement_id: string
  value_kind: ValueKind
  methodology_version: string
  valuation_method: string
  valuation_method_label?: string
  earnings_basis: {
    basis: string
    earnings_value: number
    sde: number | null
    adjusted_ebitda: number | null
    crossover_earnings_threshold: number
  } | null
  build_up_rate: {
    risk_free_rate: number
    equity_risk_premium: number
    size_premium: number
    /** "caller-supplied" or a substitution note when size_premium was omitted. */
    size_premium_source: string
    company_specific_risk: number
    long_term_growth_rate: number
    discount_rate: number
    capitalization_rate: number
    source_note: string
  }
  income_approach: {
    capitalized_earnings_value: number
    dcf_value: number
    capitalized_earnings_weight: number
    dcf_weight: number
    weighted_value: number
  }
  comparable_company: {
    low_multiple: number
    high_multiple: number
    median_multiple: number
    comp_count: number
    low_value: number
    high_value: number
    median_value: number
  }
  value_driver_score: {
    total_score: number
    max_possible_score: number
    band: string
    multiple_adjustment: number
    base_multiple: number
    adjusted_multiple: number
    /**
     * "questionnaire" when the engine derived the score from intake answers —
     * the only path the UI offers. "supplied" means a pre-B3 API client typed
     * the score in. Optional because a B2-era stored run has neither field.
     */
    score_source?: 'questionnaire' | 'supplied'
    /** The audit trail behind a "questionnaire" score: every driver, and the
     *  exact answers that produced it. Absent on the "supplied" path. */
    computed_from_answers?: Array<{
      driver: string
      score: number
      max_score: number
      answers: Array<{
        question_id: string
        section: string
        prompt: string
        answer_value: string
        answer_label: string
        points: number
      }>
    }>
  } | null
  asset_floor: {
    total_assets: number
    total_liabilities: number
    fair_value_adjustments: number
    value: number
    applied: boolean
  } | null
  triangulation: {
    income_value: number
    market_value: number
    income_weight: number
    market_weight: number
    blended_value: number
    floor_applied: boolean
    point_estimate: number
    spread_pct: number
    max_spread_pct: number
    convergence_status: string
  }
  /** The deliverable. Always a band — never a bare point estimate. */
  low: number
  high: number
  convergence_status: string
  requires_cpa_review: boolean
  revenue_multiple_check: {
    implied_multiple?: number
    low?: number
    high?: number
    in_band?: boolean
    note?: string | null
    [k: string]: unknown
  } | null
  // Profile overlay disclosure (present whenever a run plan was in play).
  industry_profile?: {
    profile_id: string
    version: string
    industry_code: string
    display_name: string
    requested_industry_code: string | null
    matched: boolean
    fallback_reason: string | null
    premise_default: string
    driver_tilts: string[]
    normalization_flags: string[]
    citations: string[]
    activated_by: string | null
    activated_on: string | null
    source_path: string | null
    notes: string[]
  }
  market_multiple?: {
    multiple_key: string
    basis: string | null
    band: { low: number; high: number; default_anchor: number; tier_label: string | null } | null
    source: string | null
    selected_multiple: number | null
  }
  cpa_overrides?: AppliedOverride[]
  overrides_out_of_range?: string[]
  // Last-recorded state echoed back by the router, informational only — NOT
  // what this run computed. VAL-INDUSTRY-WIRING-1 R2 step 1: finalize is the
  // sole writer of the row's calculated_value_*/concluded_value_* columns, so
  // these four fields carry whatever finalize last wrote (null pre-finalize),
  // never this run's own `low`/`high`. Named recorded_* — not
  // calculated_value_low/high — precisely so they can't be mistaken for the
  // engine output above.
  recorded_calculated_value_low: number | null
  recorded_calculated_value_high: number | null
  recorded_concluded_value_low: number | null
  recorded_concluded_value_high: number | null
  status: string
  /** The SSVS positioning sentence. Render it verbatim — never paraphrase. */
  positioning: string
  /** True on every /run response: this is a computed indication, never the
   *  recorded value. See recorded_value below. */
  indication_only: boolean
  /** False on every /run response — /run never persists. Only a finalize
   *  response (or a GET replaying a finalized row) represents recorded_value: true. */
  recorded_value: boolean
}
